import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Logo from "@/components/Logo";
import { Field } from "@/components/Form";
import { Eyebrow, Headline } from "@/components/Editorial";
import ProgressBar from "@/components/ProgressBar";
import { useCapture } from "@/lib/capture";
import { useAuth } from "@/lib/auth";
import { toast } from "@/components/Toast";
import { formatPhoneUS, onlyDigits, phoneToE164 } from "@/lib/format";
import { readAffiliateCookie } from "@/lib/affiliateApi";
import { hasResearchEntrySignal, clearResearchEntrySignal } from "@/lib/researchAccess";
import { C } from "@/lib/brand";

// Mirror SignIn's allowlist — keep these in sync. Open-redirect guard.
const SAFE_NEXT_PATHS = new Set(["/research"]);
function safeNextFrom(searchParams) {
  const raw = searchParams.get("next") || "";
  if (!raw.startsWith("/")) return null;
  const path = raw.split("?")[0].split("#")[0];
  return SAFE_NEXT_PATHS.has(path) ? raw : null;
}

export default function PhoneOffer() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = safeNextFrom(searchParams);
  const { data, update } = useCapture();
  const { signup } = useAuth();
  const [optIn, setOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  // Research-lane copy swap. When the visitor arrived via /research
  // (entry signal in sessionStorage), we surface the SMS opt-in as a
  // research-order status channel — physician-approval / care-team
  // language is wrong for that flow because research members never
  // see a physician. Read once at mount and snapshot, so the copy
  // doesn't shift between renders if some other component clears the
  // signal mid-flow.
  const [fromResearch] = useState(() => hasResearchEntrySignal());
  const COPY = fromResearch
    ? {
        headlineBefore: "STATUS",
        headlineItalic: "texts from VOYA9.",
        body: "And 20% off your first order. Get notified the moment your order ships, plus batch updates and account messages.",
        consent: "I agree to receive text messages from VOYA9 including order status, shipping updates, and occasional account notices. Reply STOP to opt out anytime. Standard rates apply.",
        whyAsk: "SMS is the fastest way to notify you of order shipment and account updates. We text rarely and never spam.",
      }
    : {
        headlineBefore: "TEXTS",
        headlineItalic: "from your physician.",
        body: "And 20% off your first month. Get notified the moment your application is approved, plus shipment updates and care team messages.",
        consent: "I agree to receive text messages from VOYA9 including my approval notification, shipping updates, and the occasional new program announcement. Reply STOP to opt out anytime. Standard rates apply.",
        whyAsk: "SMS is the fastest way to notify you of physician approval and order updates. We text rarely and never spam.",
      };

  const finalize = async (withPhone) => {
    setLoading(true);
    // Research-lane attribution. If the visitor entered via /research
    // (signal dropped on the research page before the sign-in
    // bounce), include `researchAccess: true` so BE auto-grants the
    // research catalogue gate on this new account. Single-shot —
    // cleared immediately after consumption so a subsequent
    // unrelated sign-up doesn't inherit the flag.
    const grantResearch = hasResearchEntrySignal();
    try {
      await signup({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        zip: data.zip,
        phone: withPhone ? phoneToE164(data.phone) : undefined,
        smsOptIn: withPhone ? optIn : false,
        signupSource: data.signupSource || "organic",
        deviceFingerprint: data.deviceFingerprint,
        turnstileToken: data.turnstileToken,
        referralCode: data.referralCode || undefined,
        // Affiliate attribution — backend reads the vy9_aff cookie ID to
        // credit the click to an affiliate. Safely null when no prior click.
        affiliateClickId: readAffiliateCookie() || undefined,
        ...(grantResearch ? { researchAccess: true } : {}),
      });
      // Consume the entry signal regardless of next-path so that a
      // future cold sign-up not initiated from /research doesn't pick
      // up the flag by accident.
      if (grantResearch) clearResearchEntrySignal();
      // Honor `next` if the member entered from a deep-linked path
      // (e.g., /research). Otherwise route through the standard
      // post-signup welcome surface.
      navigate(nextPath || "/welcome");
    } catch (err) {
      toast(err.detail || "Signup failed", "error");
      setLoading(false);
    }
  };

  const phoneDigits = (data.phone || "").replace(/\D/g, "");
  const phoneValid = phoneDigits.length >= 10;
  const canClaim = phoneValid && optIn && !loading;

  const claim = () => {
    if (!canClaim) return;
    finalize(true);
  };

  const skip = () => finalize(false);

  return (
    <div data-testid="phone-offer-page" style={{ minHeight: "100vh", background: C.ink, color: C.cream }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px" }}>
        <Link to="/"><Logo light size={22} /></Link>
        <Link to="/" data-testid="phone-close" style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: "rgba(250,246,240,0.5)", textDecoration: "none" }}>CLOSE ✕</Link>
      </header>

      <ProgressBar current={2} total={3} />

      <div style={{ maxWidth: 540, margin: "0 auto", padding: "32px 24px 96px" }}>
        {/* Hero offer card */}
        <div style={{ position: "relative", overflow: "hidden", background: C.peach, color: C.peachDark, padding: "48px 32px 40px" }}>
          <div style={{ position: "absolute", top: -10, right: -20, fontSize: 220, fontWeight: 800, color: C.peachDark, opacity: 0.12, fontFamily: '"DM Sans", sans-serif', lineHeight: 0.8, letterSpacing: "-0.06em" }}>
            20%
          </div>
          <div style={{ position: "relative", zIndex: 2 }}>
            <Eyebrow color={C.peachDark}>★ MEMBERS-ONLY OFFER</Eyebrow>
            <Headline before={COPY.headlineBefore} italic={COPY.headlineItalic} color={C.peachDark} italicColor={C.peachDark} size={48} className="mt-4" />
            <p style={{ marginTop: 20, fontSize: 15, lineHeight: 1.55, color: C.peachDark, opacity: 0.85 }}>
              {COPY.body}
            </p>
          </div>
        </div>

        <div style={{ background: C.cream, color: C.ink, padding: 28 }}>
          <Field label="MOBILE NUMBER" type="tel" value={formatPhoneUS(data.phone)} onChange={(v) => update({ phone: onlyDigits(v) })} placeholder="(555) 123-4567" testId="phone-input" autoFocus />

          <button
            type="button"
            data-testid="phone-optin-toggle"
            onClick={() => setOptIn(!optIn)}
            style={{ marginTop: 20, display: "flex", gap: 14, alignItems: "flex-start", textAlign: "left", width: "100%", cursor: "pointer" }}
          >
            <span
              style={{
                minWidth: 22,
                height: 22,
                background: optIn ? C.ink : C.pure,
                border: `1.5px solid ${C.ink}`,
                color: C.peach,
                fontSize: 14,
                fontWeight: 800,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: 2,
              }}
            >
              {optIn ? "✓" : ""}
            </span>
            <span style={{ fontSize: 12, color: C.warmGray, lineHeight: 1.55 }}>
              {COPY.consent}
            </span>
          </button>

          <div style={{ marginTop: 24 }}>
            <button
              type="button"
              data-testid="phone-claim-btn"
              onClick={claim}
              disabled={!canClaim}
              title={!canClaim ? "Add your number and confirm consent to claim 20% off." : undefined}
              style={{
                width: "100%",
                padding: "20px 32px",
                fontSize: 14,
                fontWeight: 800,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                cursor: canClaim ? "pointer" : "not-allowed",
                background: canClaim ? C.peachDeep : C.warmGray3,
                color: canClaim ? C.cream : C.warmGray,
                border: "none",
                transition: "background 160ms ease, color 160ms ease",
              }}
            >
              {loading ? "PROCESSING…" : "CLAIM 20% OFF →"}
            </button>
            {!canClaim && !loading && (
              <div data-testid="phone-claim-helper" style={{ marginTop: 10, fontSize: 12, color: C.warmGray, textAlign: "center", lineHeight: 1.5 }}>
                Add your number and confirm consent to claim 20% off.
              </div>
            )}
          </div>

          <button data-testid="phone-skip-btn" onClick={skip} disabled={loading} style={{ marginTop: 14, width: "100%", textAlign: "center", padding: 12, fontSize: 13, color: C.warmGray, textDecoration: "underline" }}>
            No thanks, continue without discount
          </button>
        </div>

        <div style={{ marginTop: 24, padding: 20, background: "rgba(250,246,240,0.08)", borderLeft: `2px solid ${C.peach}` }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peach }}>WHY WE ASK</div>
          <p style={{ marginTop: 10, fontSize: 13, color: "rgba(250,246,240,0.75)", lineHeight: 1.55 }}>
            {COPY.whyAsk}
          </p>
        </div>
      </div>
    </div>
  );
}
